import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { MapPin, Shield, Bell, AlertTriangle } from "lucide-react";

interface LocationPermissionProps {
  onPermissionGranted: () => void;
}

const LocationPermission = ({ onPermissionGranted }: LocationPermissionProps) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getErrorMessage = (error: GeolocationPositionError) => {
    switch (error.code) {
      case error.PERMISSION_DENIED:
        return "Location access was denied. Please enable location permissions in your browser and try again.";
      case error.POSITION_UNAVAILABLE:
        return "Location information is unavailable. Please check your connection and try again.";
      case error.TIMEOUT:
        return "Location request timed out. Please try again.";
      default:
        return "An unknown error occurred while requesting location access.";
    }
  };

  const requestLocation = () => {
    setIsRequesting(true);
    setError(null);
    
    // Check if running on HTTPS (required for geolocation on production domains)
    if (location.protocol !== 'https:' && location.hostname !== 'localhost' && !location.hostname.includes('lovable.app')) {
      setError("Location access requires HTTPS. Please ensure your site is served over HTTPS.");
      setIsRequesting(false);
      return;
    }
    
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by this browser.");
      setIsRequesting(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log("Location granted:", position.coords);
        setError(null);
        setIsRequesting(false);
        onPermissionGranted();
      },
      (geoError) => {
        console.error("Location error:", geoError);
        const errorMessage = getErrorMessage(geoError);
        setError(errorMessage);
        setIsRequesting(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 text-center space-y-6">
        <div className="space-y-4">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <MapPin className="h-8 w-8 text-primary" />
          </div>
          
          <div>
            <h1 className="text-2xl font-bold text-primary mb-2">
              Find Your Jummah Mosque
            </h1>
            <p className="text-muted-foreground">
              To show you nearby mosques and prayer times, we need access to your location.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 text-sm">
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
              <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
              <span>Find mosques within walking/driving distance</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
              <Bell className="h-5 w-5 text-accent flex-shrink-0" />
              <span>Get timely reminders before Jummah prayer</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-secondary/50 rounded-lg">
              <Shield className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              <span>Your location is never stored or shared</span>
            </div>
          </div>

          {error && (
            <Alert className="text-left">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-sm">
                {error}
                {error.includes("denied") && (
                  <div className="mt-2 text-xs">
                    <strong>How to enable location access:</strong><br/>
                    <strong>Chrome:</strong> Settings → Privacy and security → Site Settings → Location → Allow<br/>
                    <strong>Safari:</strong> Safari → Settings → Websites → Location → Allow for this website<br/>
                    <strong>Firefox:</strong> Click the shield/lock icon → Permissions → Location → Allow<br/>
                    <div className="mt-1">Or check your browser's location settings in Preferences/Settings</div>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Button
            onClick={requestLocation}
            disabled={isRequesting}
            className="w-full"
            size="lg"
          >
            {isRequesting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Requesting Location...
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4 mr-2" />
                Enable Location Access
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground">
            You can change this permission anytime in your browser settings.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default LocationPermission;